// Typdefinitionen + Query-Hooks für Stammdaten.
// CRUD läuft RLS-protected direkt via supabase-js — keine eigenen /api/-Endpoints nötig.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

// ---------- Products ----------

export type ProductUnit = "stueck" | "glas" | "flasche" | "beutel" | "kg" | "liter";

export interface Product {
  id: string;
  sku: string | null;
  name: string;
  description: string | null;
  unit: ProductUnit;
  price_cents: number;
  vat_rate: number;
  image_url: string | null;
  category: string | null;
  is_active: boolean;
  sort_order: number;
  /**
   * Inhalt eines Gebindes in kg (2.5, 5, 12) — NULL bei Stückware.
   *
   * Kartoffeln gibt es nur im Sack: Der Hof füllt feste Gebinde ab, und der
   * Fahrer kann nichts abwiegen. Wo das gesetzt ist, zählt die App Säcke, nicht
   * Kilo — und rechnet das Gewicht nur noch fürs Beladen daraus aus.
   */
  package_kg: number | null;
  created_at: string;
  updated_at: string;
}

export type ProductDraft = Partial<Omit<Product, "id" | "created_at" | "updated_at">> & {
  name: string;
  price_cents: number;
  unit: ProductUnit;
};

export function useProducts(opts: { onlyActive?: boolean } = {}) {
  return useQuery({
    queryKey: ["products", opts.onlyActive ?? "all"],
    queryFn: async () => {
      let q = supabase.from("products").select("*").order("sort_order").order("name");
      if (opts.onlyActive) q = q.eq("is_active", true);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as Product[];
    },
  });
}

export function useUpsertProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: ProductDraft & { id?: string }) => {
      const { data, error } = await supabase
        .from("products")
        .upsert(p, { onConflict: "id" })
        .select()
        .single();
      if (error) throw error;
      return data as Product;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["products"] }),
  });
}

export function useDeleteProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("products").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["products"] }),
  });
}

// ---------- Customers ----------

export type CustomerType = "privat" | "gewerblich";

/** Verkaufs-Marker: was der Kunde gern mitnimmt (kein fester Korbinhalt). */
export type InterestTag =
  | "gemuese"
  | "fertiggerichte"
  | "marmelade_honig"
  | "obst"
  | "gebaeck"
  | "gefluegel";

export const INTEREST_LABELS: Record<InterestTag, string> = {
  gemuese: "Gemüse",
  fertiggerichte: "Fertiggerichte",
  marmelade_honig: "Marmelade / Honig",
  obst: "Obst",
  gebaeck: "Gebäck",
  gefluegel: "Geflügel",
};

export interface Customer {
  id: string;
  profile_id: string | null;
  customer_number: string | null;
  type: CustomerType;
  display_name: string;
  email: string | null;
  phone: string | null;
  notes: string | null;
  interest_tags: InterestTag[];
  legacy_seq: number | null;
  /** Wunsch-Lieferzeit "HH:MM:SS" — gesetzt = Zeitanker der Tour. */
  preferred_time: string | null;
  time_tolerance_min: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CustomerAddress {
  id: string;
  customer_id: string;
  label: string | null;
  street: string;
  zip: string;
  city: string;
  country: string;
  geo_lat: number | null;
  geo_lng: number | null;
  delivery_notes: string | null;
  is_default: boolean;
  distance_km_from_hof: number | null;
  created_at: string;
  updated_at: string;
}

export function useCustomers(search?: string) {
  return useQuery({
    queryKey: ["customers", search ?? ""],
    queryFn: async () => {
      let q = supabase.from("customers").select("*").order("display_name");
      if (search && search.length >= 2) {
        q = q.ilike("display_name", `%${search}%`);
      }
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as Customer[];
    },
  });
}

export function useCustomer(id: string | undefined) {
  return useQuery({
    queryKey: ["customer", id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase.from("customers").select("*").eq("id", id!).single();
      if (error) throw error;
      return data as Customer;
    },
  });
}

export function useUpsertCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (c: Partial<Customer> & { display_name: string; type: CustomerType }) => {
      const { data, error } = await supabase
        .from("customers")
        .upsert(c, { onConflict: "id" })
        .select()
        .single();
      if (error) throw error;
      return data as Customer;
    },
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ["customers"] });
      qc.invalidateQueries({ queryKey: ["customer", c.id] });
    },
  });
}

export function useDeleteCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("customers").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["customers"] }),
  });
}

// ---------- Addresses ----------

export function useCustomerAddresses(customerId: string | undefined) {
  return useQuery({
    queryKey: ["addresses", customerId],
    enabled: !!customerId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("customer_addresses")
        .select("*")
        .eq("customer_id", customerId!)
        .order("is_default", { ascending: false })
        .order("created_at");
      if (error) throw error;
      return (data ?? []) as CustomerAddress[];
    },
  });
}

export function useUpsertAddress() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (a: Partial<CustomerAddress> & { customer_id: string; street: string; zip: string; city: string }) => {
      const { data, error } = await supabase
        .from("customer_addresses")
        .upsert(a, { onConflict: "id" })
        .select()
        .single();
      if (error) throw error;
      return data as CustomerAddress;
    },
    onSuccess: (a) => qc.invalidateQueries({ queryKey: ["addresses", a.customer_id] }),
  });
}

export function useDeleteAddress() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }: { id: string; customerId: string }) => {
      const { error } = await supabase.from("customer_addresses").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ["addresses", vars.customerId] }),
  });
}
